import { useT } from "@/lib/i18n";

export const PageHeader = ({ eyebrow, title, subtitle, action }) => (
  <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-8 fade-up">
    <div>
      {eyebrow && <p className="eyebrow mb-2">{eyebrow}</p>}
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="page-title">{title}</h1>
      {subtitle && <p className="text-slate-400 mt-2 max-w-2xl text-sm sm:text-base">{subtitle}</p>}
    </div>
    {action}
  </div>
);

export const Panel = ({ className = "", children, ...rest }) => (
  <div className={`rounded-lg border border-white/[0.08] bg-[#111118] p-6 transition-colors duration-200 hover:border-white/[0.15] ${className}`} {...rest}>
    {children}
  </div>
);

export const Stat = ({ label, value, hint, icon: Icon, testId, accent = "text-slate-50", delay = 0 }) => (
  <Panel className="fade-up h-full" style={{ animationDelay: `${delay}ms` }} data-testid={testId}>
    <div className="flex items-start justify-between">
      <p className="eyebrow">{label}</p>
      {Icon && <Icon className="h-4 w-4 text-slate-500" />}
    </div>
    <p className={`font-display text-3xl font-bold mt-4 tracking-tight ${accent}`} data-testid={`${testId}-value`}>{value}</p>
    {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
  </Panel>
);

export const LiveBadge = ({ live }) => {
  const { t } = useT();
  return live ? (
    <span className="inline-flex items-center gap-2 rounded-full bg-rose-600/15 border border-rose-500/30 px-3 py-1 text-xs font-semibold text-rose-300" data-testid="live-badge">
      <span className="live-dot h-2 w-2 rounded-full bg-rose-500" /> LIVE
    </span>
  ) : (
    <span className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-3 py-1 text-xs text-slate-400" data-testid="offline-badge">
      <span className="h-2 w-2 rounded-full bg-slate-500" /> {t("offline")}
    </span>
  );
};

export const Meter = ({ label, used, total, testId }) => {
  const pct = total ? Math.min(100, (used / total) * 100) : 0;
  return (
    <div data-testid={testId}>
      <div className="flex justify-between text-xs mb-2">
        <span className="text-slate-400">{label}</span>
        <span className="font-mono-ui text-slate-300">{used} / {total}</span>
      </div>
      <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
        <div className={`h-full rounded-full ${pct > 85 ? "bg-rose-500" : "bg-indigo-500"} transition-[width] duration-700`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

export const Empty = ({ icon: Icon, title, text, action, testId }) => (
  <div className="rounded-lg border border-dashed border-white/15 p-10 text-center" data-testid={testId}>
    {Icon && <Icon className="h-8 w-8 text-slate-500 mx-auto mb-4" />}
    <p className="font-display text-lg font-bold">{title}</p>
    {text && <p className="text-sm text-slate-400 mt-1 max-w-md mx-auto">{text}</p>}
    {action && <div className="mt-6">{action}</div>}
  </div>
);

export const AgentAvatar = ({ agent, size = "h-10 w-10", speaking = false }) => (
  <div className={`${size} shrink-0 rounded-full overflow-hidden bg-indigo-950 border border-white/10 flex items-center justify-center ${speaking ? "speaking-ring" : ""}`}>
    {agent?.avatar_url ? (
      <img src={agent.avatar_url} alt={agent.name} className="h-full w-full object-cover" />
    ) : (
      <span className="font-display font-bold text-indigo-200">{(agent?.name || "?").slice(0, 2).toUpperCase()}</span>
    )}
  </div>
);

export const LangToggle = () => {
  const { lang, setLang } = useT();
  return (
    <div className="inline-flex rounded-full border border-white/10 p-0.5 text-xs" data-testid="lang-toggle">
      {["it", "en"].map((l) => (
        <button key={l} onClick={() => setLang(l)} data-testid={`lang-${l}-btn`}
          className={`px-3 py-1 rounded-full uppercase transition-colors ${lang === l ? "bg-indigo-600 text-white" : "text-slate-400 hover:text-white"}`}>
          {l}
        </button>
      ))}
    </div>
  );
};
